import { useEffect, useState } from "react";
import { useSocialStore } from "../../../stores/social";
import { getMessages, sendMessage } from "../../../api/social";
import { showToastError } from "../../../services/toast";
import { useTranslation } from "react-i18next";

const Chat = () => {
  const { t } = useTranslation();
  const { idCustomer, messages, setMessages, updateSocial, setUpdateSocial } =
    useSocialStore();
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    getMessages(idCustomer)
      .then((res) => {
        setMessages(res.data);
      })
      .catch((err) => {
        showToastError(err.response.data.message);
      });
  }, [updateSocial]);

  const handleSendMessage = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    sendMessage(idCustomer, message)
      .then(() => {
        setMessage("");
        setUpdateSocial(!updateSocial);
      })
      .catch((err) => {
        showToastError(err.response.data.message);
      });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-full h-full overflow-hidden flex flex-col">
        {/* En-tête du chat - Optimisé pour PC portable */}
        <div className="bg-thead px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-1.5">
              <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-black font-semibold text-base font-Nunito">
              {t("Messagerie")}
            </h3>
          </div>
        </div>

        {/* Zone de messages - Compact */}
        <div className="flex-1 overflow-y-auto p-3 bg-gray-50 space-y-2">
          {messages?.length > 0 ? (
            messages?.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.type === "Company" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`${
                    message.type === "Company"
                      ? "bg-linear-to-br from-blue-500 to-blue-600 text-white"
                      : "bg-white border border-gray-200 text-gray-800"
                  } max-w-[80%] rounded-xl px-3 py-2 shadow-sm hover:shadow-md transition-shadow duration-200`}
                >
                  <div className="flex items-center justify-between">
                    {message.type === "Company" && <p className="text-[9px] font-medium mb-0.5 mr-2">
                      {message?.firstName} {message?.lastName}
                    </p>}
                    <p
                      className={`text-[9px] font-medium mb-0.5 ${
                        message.type === "Company" ? "text-blue-100" : "text-gray-500"
                        }`}
                        >
                      {message.date}
                    </p>
                  </div>
                  <p className="text-xs leading-relaxed whitespace-pre-wrap">
                    {message.message?.replace(/\\n/g, "\n")}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <svg className="w-12 h-12 mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-xs font-medium">{t("Aucun message")}</p>
              <p className="text-[10px] mt-1">{t("Commencez la conversation")}</p>
            </div>
          )}
        </div>

        {/* Zone de saisie - Compact */}
        <form
          className="bg-white border-t border-gray-200 p-3"
          onSubmit={(e) => handleSendMessage(e)}
        >
          <div className="flex gap-2 items-end">
            <div className="flex-1 relative">
              <textarea
                className="w-full rounded-lg p-2 pr-8 border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none resize-none transition-all duration-200 text-xs 2xl:h-44"
                placeholder={t("Écrivez votre message...")}
                rows={2}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e as any);
                  }
                }}
              />
              <div className="absolute bottom-2 right-2 text-gray-400 text-[10px]">
                ↵
              </div>
            </div>
            <button
              type="submit"
              className="group cursor-pointer bg-linear-to-r from-blue-500 to-blue-600 text-white rounded-lg px-3 py-2 hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-1.5 font-medium text-xs"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              {t("Envoyer")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Chat;
